import { insertData, updateData, fetchSqlDataWithQuery } from '../../../services/api';
import { emitAssignedTasksChanged } from './eventBus';
import { sqlNumber, sqlString } from '../../../utils/sql';

export interface CorTaskInsert {
  description: string;
  assigned_to_id: string[];
  navigate_to?: string;
  status: 'pending' | 'blocked' | 'in progress' | 'completed' | 'cancelled';
  form_record_id?: number;
  sequence?: number;
  context?: any; // jsonb context for typed metadata
  client_key?: string; // optional idempotency key
}

export type TaskPurpose = 'notify' | 'action';

export type TaskEntity = {
  kind: string;
  id: number | string;
};

export type CreateTaskInput = {
  purpose: TaskPurpose;
  entity: TaskEntity;
  assignees: string[];
  navigateTo: string;
  description: string;
  tag?: string;
  clientKey?: string;
  fanoutPerAssignee?: boolean;
};

export type CreateSequentialActionInput = {
  entity: TaskEntity;
  navigateTo: string;
  descriptionBase: string;
  steps: Array<{
    assignees: string[];
    label?: string;
    tag?: string;
    clientKey?: string;
  }>;
  fanoutPerAssignee?: boolean;
};

export type InsertTasksOptions = {
  // If true, create one row per assignee (assigned_to_id becomes a single-element array)
  // If false (default), keep one row that contains all assignees in assigned_to_id
  fanoutPerAssignee?: boolean;
};

const normalizeAssigneeId = (value: unknown): string => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  const idx = s.indexOf(' - ');
  return (idx >= 0 ? s.slice(0, idx) : s).trim();
};

const buildClientKey = (input: {
  purpose: TaskPurpose;
  entity: TaskEntity;
  tag?: string;
}): string => {
  const kind = String(input.entity?.kind || '').trim() || 'unknown';
  const id = String(input.entity?.id ?? '').trim() || 'unknown';
  const tag = String(input.tag || '').trim() || 'default';
  return `task:${input.purpose}:${kind}:${id}:${tag}`;
};

const buildContext = (input: {
  purpose: TaskPurpose;
  entity: TaskEntity;
  tag?: string;
}): Record<string, any> => {
  return {
    purpose: input.purpose,
    entity: {
      kind: String(input.entity?.kind || '').trim(),
      id: input.entity?.id,
    },
    ...(input.tag ? { tag: String(input.tag) } : {}),
  };
};

const insertTasksInternal = async (rows: CorTaskInsert[], options?: InsertTasksOptions) => {
  if (!rows || rows.length === 0) return;
  const fanout = options?.fanoutPerAssignee === true;

  const normalizedRows: CorTaskInsert[] = rows.map((r) => {
    const ids = Array.isArray(r.assigned_to_id) ? r.assigned_to_id : [];
    const normalizedIds = Array.from(new Set(ids.map(normalizeAssigneeId).filter(Boolean)));
    return {
      ...r,
      assigned_to_id: normalizedIds,
    };
  });

  // Optionally expand rows per assignee
  const expanded: CorTaskInsert[] = fanout
    ? normalizedRows.flatMap((row) => {
        const ids = Array.isArray(row.assigned_to_id) ? row.assigned_to_id : [];
        if (ids.length <= 1) return [row];
        return ids.map((pid) => ({
          ...row,
          assigned_to_id: [pid],
          // make client_key unique per assignee to avoid collisions
          client_key: row.client_key ? `${row.client_key}:assignee:${pid}` : undefined,
        }));
      })
    : normalizedRows;

  // Insert one-by-one to ensure all keys (like sequence) are persisted correctly
  for (const row of expanded) {
    await insertData('cor_tasks', row as any);

    // Fallback: if backend ignored 'sequence' during insert, enforce it via UPDATE
    if (row.sequence !== undefined && row.form_record_id !== undefined) {
      let found: any[] | undefined;
      if (row.client_key) {
        const ck = String(row.client_key);
        const byCk = `SELECT id FROM cor_tasks
                      WHERE client_key = ${sqlString(ck)}
                        AND sequence IS NULL
                      ORDER BY id DESC
                      LIMIT 1`;
        found = await fetchSqlDataWithQuery(byCk);
      }

      if (!found || !Array.isArray(found) || !found[0]?.id) {
        const desc = String(row.description || '');
        const nav = row.navigate_to ? String(row.navigate_to) : '';
        const status = String(row.status);
        const findQuery = `SELECT id FROM cor_tasks
                           WHERE form_record_id = ${sqlNumber(row.form_record_id)}
                             AND description = ${sqlString(desc)}
                             AND status = ${sqlString(status)}
                             ${nav ? `AND navigate_to = ${sqlString(nav)}` : ''}
                             AND sequence IS NULL
                           ORDER BY id DESC
                           LIMIT 1`;
        found = await fetchSqlDataWithQuery(findQuery);
      }

      const taskId = Array.isArray(found) && found[0]?.id ? Number(found[0].id) : null;
      if (taskId) {
        await updateData('cor_tasks', taskId, { sequence: Number(row.sequence) });
      }
    }
  }

  // Notify listeners to refetch assigned tasks (e.g., AppHeader badge)
  try {
    emitAssignedTasksChanged();
  } catch {}
};

export const taskService = {
  async createTask(input: CreateTaskInput) {
    if (!input) return;
    const assignees = Array.isArray(input.assignees) ? input.assignees : [];
    if (!assignees.length) return;

    const client_key = input.clientKey || buildClientKey({
      purpose: input.purpose,
      entity: input.entity,
      tag: input.tag,
    });

    const row: CorTaskInsert = {
      description: String(input.description || ''),
      assigned_to_id: assignees,
      navigate_to: String(input.navigateTo || ''),
      status: 'pending',
      context: buildContext({ purpose: input.purpose, entity: input.entity, tag: input.tag }),
      client_key,
    };

    await insertTasksInternal([row], { fanoutPerAssignee: input.fanoutPerAssignee === true });
  },

  async createTasks(inputs: CreateTaskInput[]) {
    if (!Array.isArray(inputs) || inputs.length === 0) return;
    for (const i of inputs) {
      await this.createTask(i);
    }
  },

  async notify(input: Omit<CreateTaskInput, 'purpose'>) {
    await this.createTask({ ...input, purpose: 'notify' });
  },

  async assignAction(input: Omit<CreateTaskInput, 'purpose'>) {
    await this.createTask({ ...input, purpose: 'action' });
  },

  async assignSequentialActions(input: CreateSequentialActionInput) {
    const steps = Array.isArray(input.steps) ? input.steps : [];
    if (!steps.length) return;

    const rows: CorTaskInsert[] = steps.map((s, idx) => {
      const stepNo = idx + 1;
      const desc = s.label
        ? `${String(input.descriptionBase || '').trim()} — ${String(s.label).trim()}`
        : String(input.descriptionBase || '').trim();

      const tag = s.tag || `step_${stepNo}`;
      const client_key = s.clientKey || buildClientKey({
        purpose: 'action',
        entity: input.entity,
        tag,
      });

      return {
        description: desc,
        assigned_to_id: Array.isArray(s.assignees) ? s.assignees : [],
        navigate_to: String(input.navigateTo || ''),
        status: stepNo === 1 ? 'pending' : 'blocked',
        sequence: stepNo,
        context: buildContext({ purpose: 'action', entity: input.entity, tag }),
        client_key,
      };
    });

    await insertTasksInternal(rows, { fanoutPerAssignee: input.fanoutPerAssignee !== false });
  },

  async issueSignatureApproval(input: {
    formRecordId?: number;
    fieldName: string;
    assignees: string[];
    navigateTo?: string;
    description: string;
    sequential: boolean;
    sequence: number;
    clientKey?: string;
    fanoutPerAssignee?: boolean;
  }) {
    if (!input?.fieldName) return;
    const formId = input.formRecordId;
    const seq = Number(input.sequence || 1);
    const entity: TaskEntity = {
      kind: 'form.signature',
      id: formId ?? 'unknown',
    };

    const row: CorTaskInsert = {
      description: String(input.description || ''),
      assigned_to_id: Array.isArray(input.assignees) ? input.assignees : [],
      navigate_to: input.navigateTo ? String(input.navigateTo) : undefined,
      status: input.sequential ? (seq === 1 ? 'pending' : 'blocked') : 'pending',
      form_record_id: Number.isFinite(Number(formId)) ? Number(formId) : undefined,
      sequence: input.sequential ? seq : undefined,
      context: { kind: 'signature', field: String(input.fieldName).trim(), form_record_id: Number.isFinite(Number(formId)) ? Number(formId) : undefined },
      client_key: input.clientKey,
    };

    await insertTasksInternal([row], { fanoutPerAssignee: input.fanoutPerAssignee === true });
  },

  // Mark a task completed and unblock the next sequence for same form
  async completeTaskAndUnblockNext(currentTaskId: number, actingPersonnelId: string) {
    if (!currentTaskId || !actingPersonnelId) return;

    // helper to normalize API shapes (array or {data: array})
    const firstRow = (resp: any) => {
      const arr = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      return arr && arr.length ? arr[0] : null;
    };

    // 1) Read current task info (form_record_id, sequence, status)
    const curResp = await fetchSqlDataWithQuery(
      `SELECT id, form_record_id, sequence, status FROM cor_tasks WHERE id = ${sqlNumber(currentTaskId)}`
    );
    const cur = firstRow(curResp);
    if (!cur) return;

    // Idempotency guard: if already terminal, do nothing
    const curStatus = String(cur?.status || '').toLowerCase();
    if (curStatus === 'completed' || curStatus === 'cancelled') return;

    // 2) Complete current task
    await updateData('cor_tasks', currentTaskId, {
      status: 'completed',
      last_action: actingPersonnelId,
    });

    // 3) Promote next by sequence (if sequential setup exists)
    if (cur?.form_record_id && Number.isFinite(Number(cur?.sequence))) {
      const nextResp = await fetchSqlDataWithQuery(
        `SELECT id FROM cor_tasks
         WHERE form_record_id = ${sqlNumber(cur.form_record_id)}
           AND sequence = ${Number(cur.sequence) + 1}
           AND status = 'blocked'
         ORDER BY id ASC
         LIMIT 1`
      );
      const next = firstRow(nextResp);
      if (next?.id) {
        await updateData('cor_tasks', next.id, { status: 'pending' });
      }
    }

    // 4) Notify listeners
    try { emitAssignedTasksChanged(); } catch {}
  },

  async completeTaskById(taskId: number, actingPersonnelId: string) {
    if (!taskId || !actingPersonnelId) return;

    const rows = await fetchSqlDataWithQuery(
      `SELECT id, status FROM cor_tasks WHERE id = ${sqlNumber(taskId)} LIMIT 1`
    );
    const cur = Array.isArray(rows) && rows[0] ? rows[0] : null;
    if (!cur) return;

    const curStatus = String(cur?.status || '').toLowerCase();
    if (curStatus === 'completed' || curStatus === 'cancelled') return;

    await updateData('cor_tasks', Number(taskId), {
      status: 'completed',
      last_action: String(actingPersonnelId),
    });

    try { emitAssignedTasksChanged(); } catch {}
  },

  // Convenience: complete by (form_record_id, fieldName embedded in description)
  async completeByFormAndField(formRecordId: number, fieldName: string, actingPersonnelId: string) {
    if (!formRecordId || !fieldName || !actingPersonnelId) return;
    const safeField = String(fieldName);
    // Prefer jsonb context identity (newer tasks), fallback to legacy description suffix matching
    const q = `SELECT id FROM cor_tasks
               WHERE form_record_id = ${sqlNumber(formRecordId)}
                 AND status IN ('pending','in progress')
                 AND (
                   (context->>'kind' = 'signature' AND context->>'field' = ${sqlString(safeField)})
                   OR description LIKE ${sqlString(`% field:${safeField}`)}
                 )
               ORDER BY sequence NULLS LAST, id ASC
               LIMIT 1`;
    const rows = await fetchSqlDataWithQuery(q);
    const taskId = Array.isArray(rows) && rows[0]?.id ? Number(rows[0].id) : null;
    if (taskId) {
      await this.completeTaskAndUnblockNext(taskId, actingPersonnelId);
    }
  },
};
